import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { AlertCircle, Building2, ChevronRight, Copy, ExternalLink, FileText, Layers, Loader2, Search } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useAuth } from '@/contexts/AuthContext';
import { cn } from '@/lib/utils';
import toastService from '@/services/toast/service';
import {
  lookupService,
  type LookupDetail,
  type LookupKind,
  type LookupMatch,
  type LookupResult,
  type LookupType,
} from '@/services/lookup';

const TYPE_GROUPS: Array<{ label: string | null; items: Array<[LookupType, string]> }> = [
  { label: null, items: [['auto', 'Auto-detect']] },
  {
    label: 'Platform',
    items: [
      ['agency', 'Agency'],
      ['subaccount', 'Sub-account'],
      ['form', 'Form'],
      ['user', 'User'],
      ['lead', 'Lead'],
      ['submission', 'Submission'],
      ['appointment', 'Appointment'],
    ],
  },
  { label: 'Billing', items: [['stripe_customer', 'Stripe customer'], ['stripe_subscription', 'Stripe subscription']] },
  {
    label: 'Calendars and CRM',
    items: [['ghl_location', 'GHL location'], ['ghl_calendar', 'GHL calendar'], ['calendly_event_type', 'Calendly event type']],
  },
];
const TYPES = new Set<string>(TYPE_GROUPS.flatMap((g) => g.items.map(([value]) => value)));

const KIND_LABELS: Record<LookupKind, string> = {
  agency: 'Agency',
  subaccount: 'Sub-account',
  form: 'Form',
  user: 'User',
  lead: 'Lead',
  submission: 'Submission',
  appointment: 'Appointment',
  integration: 'Integration',
  ending: 'Ending',
  element: 'Question',
  webhook: 'Webhook',
  split_test: 'Split test',
};

const PASTE_GUIDE: Array<[string, string, string]> = [
  [
    'Public ID',
    'Looked up in agencies, sub-accounts, forms, questions, endings, users, leads, submissions, appointments, integrations, webhooks and split tests.',
    '036427e1-8c2f-...',
  ],
  ['Number', 'Could be an agency, sub-account, form, user, lead, submission or appointment. You get every match, or pick a type first.', '1796'],
  ['Email', 'Matches users and leads.', 'name@example.com'],
  ['Stripe', 'Customer or subscription ID, on its own or inside a Stripe dashboard URL.', 'cus_...  sub_...'],
  ['Agency slug', 'The slug shown on the agency page.', 'northwind-growth'],
  ['HighLevel', 'Location or calendar ID, on its own, in a HighLevel URL or in a whole ghl.ERROR payload.', 'Zr4kP0aW9mQx1bTn7LcE'],
  ['Calendly', 'Event type URL from a calendly.ERROR line.', 'https://api.calendly.com/event_types/...'],
];

type Tone = 'success' | 'warning' | 'danger' | 'neutral';

function statusTone(status: string | null | undefined): Tone {
  const s = (status ?? '').toLowerCase();
  if (['active', 'published', 'trialing', 'complete', 'confirmed', 'running'].includes(s)) return 'success';
  if (['deleted', 'deactivated', 'token_revoked', 'canceled', 'cancelled', 'error', 'past_due', 'unpaid', 'locked'].includes(s)) return 'danger';
  if (['pending', 'partial', 'paused', 'incomplete'].includes(s)) return 'warning';
  return 'neutral';
}

function providerLabel(provider: string): string {
  if (provider === 'go_high_level' || provider === 'ghl') return 'HighLevel';
  if (provider === 'calendly') return 'Calendly';
  if (provider === 'google') return 'Google Calendar';
  return provider;
}

/** Agency owners are stored as super_admin; sub-account owners as owner. */
function roleLabel(scope: 'agency' | 'subaccount', role: string): { label: string; owner: boolean } {
  const owner = scope === 'agency' ? role === 'super_admin' : role === 'owner';
  if (owner) return { label: 'Owner', owner };
  return { label: role.charAt(0).toUpperCase() + role.slice(1).replace(/_/g, ' '), owner };
}

/** A pasted URL or log line can be long; repeat only its start. */
function shortQuery(q: string): string {
  return q.length > 80 ? `${q.slice(0, 80)}...` : q;
}

function useFormatDate() {
  const { user } = useAuth();
  const timeZone = user?.timezone || undefined;
  return (iso: string | null | undefined, withTime = false) => {
    if (!iso) return null;
    return new Date(iso).toLocaleString('en-US', {
      timeZone,
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      ...(withTime ? { hour: 'numeric', minute: '2-digit' } : {}),
    });
  };
}

export default function LookupPage() {
  const [params, setParams] = useSearchParams();
  const q = params.get('q') ?? '';
  const typeParam = params.get('type');
  const type: LookupType = typeParam && TYPES.has(typeParam) ? (typeParam as LookupType) : 'auto';
  const select = params.get('select');

  const [draft, setDraft] = useState(q);
  const [draftType, setDraftType] = useState<LookupType>(type);
  const [result, setResult] = useState<LookupResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Bumped on every submit, so looking up the same text again re-runs it.
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    setDraft(q);
    setDraftType(type);
    // A new query must not show the previous query's results while it loads.
    setResult(null);
  }, [q, type]);

  useEffect(() => {
    if (!q.trim()) {
      setResult(null);
      setError(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    lookupService
      .lookup({ q, type, select })
      .then((r) => {
        if (!cancelled) setResult(r);
      })
      .catch((err) => {
        if (cancelled) return;
        setResult(null);
        setError(err.response?.data?.message || "Couldn't run the lookup");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [q, type, select, reloadKey]);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const value = draft.trim();
    if (!value) return;
    setParams(draftType === 'auto' ? { q: value } : { q: value, type: draftType });
    setReloadKey((k) => k + 1);
  };

  const pick = (m: LookupMatch) => {
    const next = new URLSearchParams(params);
    next.set('select', `${m.kind}:${m.id}`);
    setParams(next);
  };

  let content: ReactNode;
  if (!q) content = <PasteGuide />;
  else if (loading && !result) {
    content = (
      <div role="status" className="flex justify-center py-16">
        <Loader2 aria-hidden className="size-6 animate-spin text-muted-foreground" />
        <span className="sr-only">Looking up</span>
      </div>
    );
  } else if (error) {
    content = (
      <Card className="py-4 px-5 flex-row items-center gap-3 text-sm">
        <AlertCircle className="size-4 text-destructive" />
        {error}
      </Card>
    );
  } else if (result && result.matches.length === 0) {
    content = (
      <Card className="py-10 px-5 items-center text-center gap-2">
        <div className="font-medium break-all">Nothing matches "{shortQuery(q)}"</div>
        <p className="text-sm text-muted-foreground max-w-md">
          Check for a typo or a cut-off ID.
        </p>
      </Card>
    );
  } else if (result && result.matches.length > 1) {
    content = <MatchList key={`${q}|${type}`} q={q} matches={result.matches} truncated={result.truncated} onPick={pick} />;
  } else if (result && result.detail) {
    content = <MatchDetail match={result.matches[0]} detail={result.detail} />;
  }

  return (
    <div className="space-y-4 sm:space-y-6 w-full max-w-[1310px] mx-auto px-4 sm:px-0">
      <div>
        <h1 className="text-xl sm:text-2xl font-semibold tracking-tight">Lookup</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Paste any ID from a log line, a URL or Stripe to see which agency, sub-account and form it belongs to.
        </p>
      </div>

      <form onSubmit={submit} className="flex flex-col sm:flex-row gap-2">
        <Select value={draftType} onValueChange={(v) => setDraftType(v as LookupType)}>
          <SelectTrigger className="sm:w-[200px] bg-background" aria-label="ID type">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {TYPE_GROUPS.map((group) => (
              <SelectGroup key={group.label ?? 'auto'}>
                {group.label && <SelectLabel>{group.label}</SelectLabel>}
                {group.items.map(([value, label]) => (
                  <SelectItem key={value} value={value}>{label}</SelectItem>
                ))}
              </SelectGroup>
            ))}
          </SelectContent>
        </Select>
        <Input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="Paste an ID, email, Stripe ID or GHL ID"
          aria-label="ID to look up"
          className="font-mono flex-1 bg-background"
          autoFocus
        />
        <Button type="submit" disabled={!draft.trim() || loading}>
          {loading ? <Loader2 className="animate-spin" /> : <Search />}
          Look up
        </Button>
      </form>

      {content}
    </div>
  );
}

function PasteGuide() {
  return (
    <Card className="py-5 px-5 gap-1">
      <h2 className="font-semibold text-[15px] mb-2">What you can paste</h2>
      {PASTE_GUIDE.map(([label, text, example]) => (
        <div key={label} className="grid sm:grid-cols-[140px_minmax(0,1fr)] gap-x-3 gap-y-0.5 py-2 border-b last:border-b-0 text-sm">
          <span className="text-muted-foreground">{label}</span>
          <span>
            {text} <span className="font-mono text-xs text-muted-foreground break-all">{example}</span>
          </span>
        </div>
      ))}
      <p className="text-sm text-muted-foreground mt-2">
        A whole URL or log line works too. The public IDs, Stripe IDs and HighLevel IDs in it are looked up, up to 5 of each.
      </p>
    </Card>
  );
}

function pathText(m: LookupMatch) {
  return [m.path.agency?.name, m.path.subaccount?.name, m.path.form?.name].filter(Boolean).join(' › ');
}

function MatchList({
  q, matches, truncated, onPick,
}: { q: string; matches: LookupMatch[]; truncated: boolean; onPick: (m: LookupMatch) => void }) {
  const [kind, setKind] = useState<LookupKind | null>(null);
  const kinds = [...new Set(matches.map((m) => m.kind))];
  const shown = kind ? matches.filter((m) => m.kind === kind) : matches;

  return (
    <div className="space-y-4">
      <div className="flex items-start gap-2.5 rounded-lg border border-amber-200 bg-amber-50 px-3.5 py-3 text-sm text-amber-900 dark:border-amber-900/50 dark:bg-amber-900/20 dark:text-amber-200">
        <AlertCircle className="size-4 mt-0.5 shrink-0" />
        <span>
          <strong className="break-all">{shortQuery(q)}</strong> matches {matches.length} records. Pick the one you meant, or set the ID type to narrow it.
        </span>
      </div>

      {kinds.length > 1 && (
        <div className="flex flex-wrap gap-1.5" role="group" aria-label="Filter by type">
          <Button size="sm" variant={kind === null ? 'default' : 'outline'} aria-pressed={kind === null} className="rounded-full h-8" onClick={() => setKind(null)}>
            All {matches.length}
          </Button>
          {kinds.map((k) => (
            <Button key={k} size="sm" variant={kind === k ? 'default' : 'outline'} aria-pressed={kind === k} className="rounded-full h-8" onClick={() => setKind(k)}>
              {KIND_LABELS[k]}
            </Button>
          ))}
        </div>
      )}

      <Card className="py-0 gap-0 overflow-hidden">
        {shown.map((m) => (
          <button
            key={`${m.kind}:${m.id}`}
            type="button"
            onClick={() => onPick(m)}
            className="grid grid-cols-[minmax(0,1fr)_auto] sm:grid-cols-[170px_minmax(0,1fr)_auto] items-center gap-x-4 gap-y-1 px-4 sm:px-5 py-3.5 text-left border-b last:border-b-0 hover:bg-muted/50 transition-colors"
          >
            <Badge variant="outline" className="rounded-md font-medium col-span-2 sm:col-span-1">
              {KIND_LABELS[m.kind]} #{m.id}
            </Badge>
            <div className="min-w-0">
              <div className="text-sm font-medium truncate">{m.title}</div>
              <div className="text-xs text-muted-foreground truncate">
                {[pathText(m), m.matchedBy].filter(Boolean).join(' · ')}
              </div>
            </div>
            <ChevronRight className="size-4 text-muted-foreground" />
          </button>
        ))}
      </Card>
      <p className="text-sm text-muted-foreground">
        {truncated && 'Some lists stop at the first 20 matches, so there are more than shown. '}
        Open a row to see its agency, sub-account, form, people and integrations.
      </p>
    </div>
  );
}

/** Which row of the matched card holds the value that was pasted. */
function hitRow(matchedBy: string): 'id' | 'publicId' | 'slug' | 'stripe' | 'subscription' | null {
  if (matchedBy.endsWith('public ID')) return 'publicId';
  if (matchedBy === 'Agency slug') return 'slug';
  if (matchedBy === 'Stripe customer ID') return 'stripe';
  if (matchedBy === 'Stripe subscription ID') return 'subscription';
  if (matchedBy.endsWith(' ID') && !matchedBy.startsWith('Stripe')) return 'id';
  return null;
}

function MatchDetail({ match, detail }: { match: LookupMatch; detail: LookupDetail }) {
  const [params, setParams] = useSearchParams();
  const formatDate = useFormatDate();
  const chainKind = match.kind === 'agency' || match.kind === 'subaccount' || match.kind === 'form';
  const hit = (kind: LookupKind) => (match.kind === kind ? hitRow(match.matchedBy) : null);
  const { agency, subaccount, form } = detail;

  const backToList = () => {
    const next = new URLSearchParams(params);
    next.delete('select');
    setParams(next);
  };

  const chain = [
    agency && (
      <ChainCard
        key="agency"
        icon={<Building2 className="size-3.5" />}
        label="Agency"
        matched={match.kind === 'agency'}
        badge={<Badge tone={statusTone(agency.stripeStatus ?? agency.status)}>{[agency.stripeStatus ?? agency.status, agency.plan].filter(Boolean).join(' · ')}</Badge>}
        title={agency.name}
        link={{ to: `/agencies/${agency.publicId}`, label: 'Open agency' }}
      >
        <IdRow label="ID" value={String(agency.id)} copy highlight={hit('agency') === 'id'} />
        <IdRow label="Public ID" value={agency.publicId} copy highlight={hit('agency') === 'publicId'} />
        <IdRow label="Slug" value={agency.slug} highlight={hit('agency') === 'slug'} />
        <IdRow label="Stripe" value={agency.stripeCustomerId} copy highlight={hit('agency') === 'stripe'} />
        <IdRow label="Subscription" value={agency.stripeSubscriptionId} copy highlight={hit('agency') === 'subscription'} />
        <IdRow label="Created" value={formatDate(agency.createdAtUtc)} />
      </ChainCard>
    ),
    subaccount && (
      <ChainCard
        key="subaccount"
        icon={<Layers className="size-3.5" />}
        label="Sub-account"
        matched={match.kind === 'subaccount'}
        badge={<Badge tone={statusTone(subaccount.status)}>{subaccount.status}</Badge>}
        title={subaccount.name}
        link={agency ? { to: `/agencies/${agency.publicId}`, label: 'Open in agency page' } : undefined}
      >
        <IdRow label="ID" value={String(subaccount.id)} copy highlight={hit('subaccount') === 'id'} />
        <IdRow label="Public ID" value={subaccount.publicId} copy highlight={hit('subaccount') === 'publicId'} />
        <IdRow label="Forms" value={`${subaccount.formsCount} (${subaccount.publishedFormsCount} published)`} />
        <IdRow label="Created" value={formatDate(subaccount.createdAtUtc)} />
      </ChainCard>
    ),
    form && (
      <ChainCard
        key="form"
        icon={<FileText className="size-3.5" />}
        label="Form"
        matched={match.kind === 'form'}
        badge={<Badge tone={statusTone(form.status)}>{form.status}</Badge>}
        title={form.name}
      >
        <IdRow label="ID" value={String(form.id)} copy highlight={hit('form') === 'id'} />
        <IdRow label="Public ID" value={form.publicId} copy highlight={hit('form') === 'publicId'} />
        <IdRow label="Skin" value={form.skin} />
        <IdRow label="Completed" value={String(form.completedSubmissions)} />
        <IdRow label="Last completed" value={formatDate(form.lastCompletedAtUtc)} />
      </ChainCard>
    ),
  ].filter(Boolean);

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
        <Badge variant="outline" className="font-medium">{match.matchedBy}</Badge>
        {params.get('select') ? (
          <Button variant="link" size="sm" className="h-auto p-0" onClick={backToList}>Back to all matches</Button>
        ) : (
          <span>1 match</span>
        )}
      </div>

      {!chainKind && <MatchedRecord match={match} />}

      {chain.length > 0 && (
        <div className="flex flex-col lg:flex-row lg:items-stretch gap-3">
          {chain.map((card, i) => (
            <div key={i} className="contents">
              {i > 0 && (
                <div className="hidden lg:flex items-center text-muted-foreground/60">
                  <ChevronRight className="size-4" />
                </div>
              )}
              {card}
            </div>
          ))}
        </div>
      )}

      <div className="grid gap-5 lg:grid-cols-2">
        {detail.people.length > 0 && (
          <Card className="py-5 px-5 gap-2 min-w-0">
            <h2 className="font-semibold text-[15px]">People</h2>
            <div>
              {detail.people.map((p) => (
                <div key={`${p.scope}:${p.id}`} className="flex items-center gap-3 py-2.5 border-b last:border-b-0">
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium truncate">{p.name || p.email}</div>
                    <div className="text-xs text-muted-foreground truncate">
                      {p.email} · user {p.id}{p.scope === 'subaccount' ? ' · this sub-account only' : ''}
                    </div>
                  </div>
                  <Badge variant={roleLabel(p.scope, p.role).owner ? 'default' : 'outline'}>{roleLabel(p.scope, p.role).label}</Badge>
                  <Link to={`/users/${p.publicId}`} className="text-sm font-medium hover:underline">Open user</Link>
                </div>
              ))}
            </div>
          </Card>
        )}

        {subaccount && (
          <Card className="py-5 px-5 gap-2 min-w-0">
            <h2 className="font-semibold text-[15px]">Sub-account integrations</h2>
            <div>
              {detail.integrations.length === 0 && (
                <p className="text-sm text-muted-foreground py-2.5">No integrations connected.</p>
              )}
              {detail.integrations.map((i) => (
                <div key={i.provider} className="grid grid-cols-[110px_minmax(0,1fr)_auto] items-center gap-3 py-2.5 border-b">
                  <span className="text-sm font-medium">{providerLabel(i.provider)}</span>
                  <div className="min-w-0">
                    <div className="text-sm truncate">{i.name}</div>
                    {i.locationId && <div className="font-mono text-xs text-muted-foreground truncate">location {i.locationId}</div>}
                  </div>
                  <Badge tone={statusTone(i.status)}>{i.status}</Badge>
                </div>
              ))}
              {detail.webhooks && (
                <div className="grid grid-cols-[110px_minmax(0,1fr)] items-center gap-3 py-2.5">
                  <span className="text-sm font-medium">Webhooks</span>
                  <span className="text-sm">
                    {detail.webhooks.total === 0 ? 'None' : `${detail.webhooks.active} active of ${detail.webhooks.total}`}
                  </span>
                </div>
              )}
            </div>
          </Card>
        )}
      </div>

      {detail.calendarEndings.length > 0 && (
        <Card className="py-5 px-5 gap-2 min-w-0">
          <h2 className="font-semibold text-[15px]">{form ? "This form's calendar endings" : "Calendar endings on this sub-account's forms"}</h2>
          <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[640px]">
            <thead>
              <tr className="text-left text-xs text-muted-foreground border-b">
                {!form && <th className="py-2 font-medium">Form</th>}
                <th className="py-2 font-medium">Ending</th>
                <th className="py-2 font-medium">Provider</th>
                <th className="py-2 font-medium">Calendar ID</th>
                <th className="py-2 font-medium">Calendar health</th>
              </tr>
            </thead>
            <tbody>
              {detail.calendarEndings.map((e) => (
                <tr key={e.endingId} className="border-b last:border-b-0">
                  {!form && <td className="py-2.5 pr-3">{e.form.name} <span className="font-mono text-xs text-muted-foreground">#{e.form.id}</span></td>}
                  <td className="py-2.5 pr-3">{e.endingName}</td>
                  <td className="py-2.5 pr-3">{providerLabel(e.provider)}</td>
                  <td className="py-2.5 pr-3 font-mono text-xs break-all">{e.calendarId ?? 'Not set'}</td>
                  <td className="py-2.5">
                    {e.calendarStatus ? <Badge tone={statusTone(e.calendarStatus)}>{e.calendarStatus}</Badge> : <span className="text-muted-foreground">Not checked</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </Card>
      )}
    </div>
  );
}

function ChainCard({
  icon, label, matched, badge, title, link, children,
}: {
  icon: ReactNode;
  label: string;
  matched: boolean;
  badge: ReactNode;
  title: string;
  link?: { to: string; label: string };
  children: ReactNode;
}) {
  return (
    <Card className={cn('flex-1 min-w-0 py-4 px-4 gap-3', matched && 'border-foreground ring-1 ring-foreground')}>
      <div className="flex items-center justify-between gap-2">
        <div className={cn('flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide', matched ? 'text-foreground' : 'text-muted-foreground')}>
          {icon}
          {label}
          {matched && ' · matched'}
        </div>
        {badge}
      </div>
      <div className="text-[17px] font-semibold leading-snug break-words">{title}</div>
      <div>{children}</div>
      {link && (
        <Link to={link.to} className="mt-auto inline-flex items-center gap-1 text-sm font-medium hover:underline">
          {link.label}
          <ExternalLink className="size-3.5" />
        </Link>
      )}
    </Card>
  );
}

function MatchedRecord({ match }: { match: LookupMatch }) {
  const formatDate = useFormatDate();
  return (
    <Card className="py-4 px-4 gap-3 border-foreground ring-1 ring-foreground">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="text-xs font-semibold uppercase tracking-wide">{KIND_LABELS[match.kind]} · matched</div>
        {match.kind === 'user' && match.publicId && (
          <Link to={`/users/${match.publicId}`} className="inline-flex items-center gap-1 text-sm font-medium hover:underline">
            Open user
            <ExternalLink className="size-3.5" />
          </Link>
        )}
      </div>
      <div>
        <div className="text-[17px] font-semibold leading-snug break-words">{match.title}</div>
        {match.subtitle && <div className="text-sm text-muted-foreground">{match.subtitle}</div>}
      </div>
      <div className="grid gap-x-8 md:grid-cols-2">
        <IdRow label="ID" value={String(match.id)} copy />
        {match.publicId && <IdRow label="Public ID" value={match.publicId} copy />}
        {match.fields.map((f) => (
          <IdRow key={f.label} label={f.label} value={f.date ? formatDate(f.value, true) : f.value} copy={f.mono} />
        ))}
      </div>
    </Card>
  );
}

function IdRow({ label, value, copy, highlight }: { label: string; value: string | null; copy?: boolean; highlight?: boolean }) {
  return (
    <div className="grid grid-cols-[96px_minmax(0,1fr)_28px] items-center gap-x-2 min-h-7">
      <span className="text-xs text-muted-foreground">{label}</span>
      {value ? (
        <span
          title={value}
          className={cn(
            'text-sm truncate',
            copy && 'font-mono text-[12.5px]',
            highlight && 'bg-yellow-100 dark:bg-yellow-900/40 rounded px-1 -mx-1',
          )}
        >
          {value}
        </span>
      ) : (
        <span className="text-sm text-muted-foreground">Not set</span>
      )}
      {copy && value ? <CopyButton value={value} label={label} /> : <span />}
    </div>
  );
}

function CopyButton({ value, label }: { value: string; label: string }) {
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      toastService.success('Copied');
    } catch {
      toastService.error("Couldn't copy");
    }
  };
  return (
    <Button type="button" variant="ghost" size="icon-xs" className="text-muted-foreground" onClick={copy} aria-label={`Copy ${label}`}>
      <Copy />
    </Button>
  );
}
